import { Box } from '@mui/material'
import { GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { groupBy } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import {
  Factsheet,
  Factsheet_Assessment,
  Pf_Kap_Project,
  Pf_Pgv_Project,
  Project_Assessment,
  Query_Root,
} from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import { AssessorManagementGrid } from 'src/screens/administration/round-management/details/assessor-management/AssessorManagementGrid'
import { ProjectAssessorManagementModal } from 'src/screens/shared/application/assessment/assessor-management/ProjectAssessorManagementModal'
import {
  getProjectsAndAssessorsQuery,
  getRoundInformationById,
} from 'src/screens/administration/round-management/round-management-queries'
import { FactsheetAssessorManagementModal } from 'src/screens/shared/application/assessment/assessor-management/FactsheetAssessorManagementModal'
import { AddButton } from 'src/shared/button/Buttons'
import { PROCESS, PROCESS_TYPE } from 'src/shared/constants/constants'
import { AddIcon } from 'src/shared/icons/Icons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { RoundManagementExportMenu } from 'src/shared/menu/RoundManagementExportMenu'
import { ModalDialog } from 'src/shared/modal-dialog/ModalDialog'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { Section } from 'src/shared/presentation/Section'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useUserLocale } from 'src/user/UserContext'
import { useClient, useQuery } from 'urql'

export interface ProjectViewModel {
  id: number
  pid: number
  project: string
  process: PROCESS_TYPE
}

export interface AssessmentViewModel {
  entityId: number
  instance: string
  instance_key: string
  process: string
  user: {
    firstName: string
    lastName: string
    email: string
  }
}

export const AssessorManagementPage = () => {
  const navigate = useDelayedNavigate()
  const { roundId } = useParams()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const { getMessage } = useMessageSource()
  const locale = useUserLocale()

  const [projects, setProjects] = useState<ProjectViewModel[]>([])
  const [openModal, setOpenModal] = useState<boolean>(false)

  const onBackHandler = () => {
    navigate(`/${ROUTES.RoundManagementIndex.path}`)
  }

  const [{ data, fetching }] = useQuery<{
    funding_round: Query_Root['funding_round']
  }>({
    query: getRoundInformationById,
    variables: { roundId },
  })

  const roundName = data?.funding_round?.[0].name ?? ''
  const process = useMemo(() => data?.funding_round?.[0].process as PROCESS_TYPE, [data])
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([])

  const projectToProjectViewModel = (
    projects: Pf_Kap_Project[] | Pf_Pgv_Project[],
    process: PROCESS_TYPE,
  ): ProjectViewModel[] =>
    projects.map((p) => ({
      id: p.project_base.id,
      pid: p.id,
      project: p.project_base.dossier.short_title,
      process: process,
    }))

  const factsheetToProjectViewModel = (factsheets: Factsheet[]): ProjectViewModel[] =>
    factsheets.map((f) => ({ id: f.id, pid: f.id, project: f.title, process: PROCESS.FACTSHEET }))

  const projectAssessmentToAssessmentViewModel = (assessments: Project_Assessment[]): AssessmentViewModel[] =>
    assessments.map((a) => ({
      entityId: a.project_base_id,
      instance: a.assessment_instance_type.code,
      instance_key: a.assessment_instance_type.key,
      process: a.assessment_instance_type.process,
      user: {
        firstName: a.user.first_name ?? '',
        lastName: a.user.last_name ?? '',
        email: a.user.email ?? '',
      },
    }))

  const factsheetAssessmentToAssessmentViewModel = (assessments: Factsheet_Assessment[]): AssessmentViewModel[] =>
    assessments.map((a) => ({
      entityId: a.factsheet_id,
      instance: a.assessment_instance_type.code,
      instance_key: a.assessment_instance_type.key,
      process: a.assessment_instance_type.process,
      user: {
        firstName: a.user.first_name ?? '',
        lastName: a.user.last_name ?? '',
        email: a.user.email ?? '',
      },
    }))

  const initData = useCallback(async () => {
    if (process !== null && process !== undefined && roundId !== undefined && roundId !== null) {
      const { data, error } = await urqlClient
        .query<{
          project_assessment: Query_Root['project_assessment']
          factsheet_assessment: Query_Root['factsheet_assessment']
          pf_kap_project: Query_Root['pf_kap_project']
          pf_pgv_project: Query_Root['pf_pgv_project']
          factsheet: Query_Root['factsheet']
        }>(getProjectsAndAssessorsQuery, {
          process: process,
          isPfKap: process === PROCESS.PF_KAP,
          isPfPgv: process === PROCESS.PF_PGV,
          isFactsheet: process === PROCESS.FACTSHEET,
          roundId,
          locale,
        })
        .toPromise()

      if (data) {
        let projects: ProjectViewModel[] = []

        if (process === PROCESS.PF_KAP && data?.pf_kap_project) {
          projects = projectToProjectViewModel(data?.pf_kap_project, PROCESS.PF_KAP)
        } else if (process === PROCESS.PF_PGV && data?.pf_pgv_project) {
          projects = projectToProjectViewModel(data?.pf_pgv_project, PROCESS.PF_PGV)
        } else if (process === PROCESS.FACTSHEET && data?.factsheet) {
          projects = factsheetToProjectViewModel(data.factsheet)
        }

        const assessments =
          process === PROCESS.FACTSHEET
            ? factsheetAssessmentToAssessmentViewModel(data.factsheet_assessment)
            : projectAssessmentToAssessmentViewModel(data?.project_assessment)

        const assessmentManagementList = projects.map((project) => ({
          ...project,
          ...groupBy(
            assessments.filter((assessment) => assessment.entityId === project.id),
            'instance',
          ),
        }))

        setProjects(assessmentManagementList)
      } else if (!fetching && error) {
        notificationService.operationFailed()
      }
    }
  }, [urqlClient, process, roundId, locale, fetching, notificationService])

  useEffect(() => {
    initData()
  }, [initData])

  const onSuccess = () => {
    initData()
    setOpenModal(false)
  }

  const onRowSelection = (selection: GridRowSelectionModel) => {
    setSelectionModel(selection)
  }

  return (
    <ScreenLayout title={roundName} onBack={onBackHandler} actions={<RoundManagementExportMenu process={process} />}>
      <PageLayout>
        <Section
          id="assessor-management"
          title={getMessage('label.navigation.assessor.management')}
          actionButton={
            <AddButton
              messageKey={getMessage('label.add.assessors')}
              startIcon={<AddIcon />}
              onClick={() => setOpenModal(true)}
              disabled={selectionModel.length === 0}
            />
          }
          helpAndInstructions={<HelpAndInstructions labelKey="label.help.assessor.management" />}
        >
          <Box mt={2}>
            <AssessorManagementGrid projects={projects} onRowSelection={onRowSelection} />
          </Box>
        </Section>
        <ModalDialog
          open={openModal}
          title={getMessage('label.add.assessors')}
          maxWidth="sm"
          withCloseIcon={false}
          onClose={() => setOpenModal(false)}
        >
          {process === PROCESS.FACTSHEET ? (
            <FactsheetAssessorManagementModal
              onCancel={() => setOpenModal(false)}
              onDiscard={() => setOpenModal(false)}
              onSuccess={onSuccess}
              selectedFactsheetIds={selectionModel as number[]}
            />
          ) : (
            <ProjectAssessorManagementModal
              onCancel={() => setOpenModal(false)}
              onSuccess={onSuccess}
              process={process}
              selections={selectionModel}
            />
          )}
        </ModalDialog>
      </PageLayout>
    </ScreenLayout>
  )
}
